import React from 'react';
import classNames from 'classnames';

import BuilderHeader from 'components/Builder/DealBuilder/Header';
import BuilderLine from 'components/Builder/DealBuilder/Line';
import ScreenReaderAnnouncement from 'components/ScreenReaderAnnouncement';
import HeaderMessage from 'components/Builder/DealBuilder/utils/HeaderMessage';

import { useBuilderCollapse } from 'hooks/use-builder-collapse';
import ExpandButton from './ExpandButton';

import styles from './Builder.scss';
import { BaseBuilder, BaseGroupItemPlaceholder } from '../types';

interface DealBuilderProps<T extends BaseBuilder = BaseBuilder> {
  alwaysOpen?: boolean;
  builder: T;
  length?: number;
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  location?: string;
  completeMessage?: string;
  multipleMessage?: string;
  initialMessage?: string;
  completeIcon?: React.ReactElement;
  defaultIcon?: React.ReactElement;
}

const DealBuilder = ({
  alwaysOpen,
  builder,
  length = 1,
  GroupItemPlaceholder,
  location,
  completeIcon,
  defaultIcon,
  initialMessage,
  multipleMessage,
  completeMessage,
}: DealBuilderProps) => {
  const { completed, groups, savings } = builder;
  const { collapsed, onClick } = useBuilderCollapse({
    alwaysOpen,
    completed,
  });

  const message = HeaderMessage({
    completed,
    length,
    complete: completeMessage,
    initial: initialMessage,
    multiple: multipleMessage,
  });

  if (!builder) {
    return null;
  }

  return (
    <div
      className={classNames(styles.builder, { [styles.completed]: completed })}
      data-testid="deal-builder"
    >
      <div className={styles.container}>
        {completed && <ScreenReaderAnnouncement message={`${location} completed`} />}
        <BuilderHeader
          completed={completed}
          completeIcon={completeIcon}
          defaultIcon={defaultIcon}
          savings={savings}
          message={message}
        />
        <div className={classNames(styles.linesWrapper, { [styles.expanded]: !collapsed })}>
          <div className={styles.builderLines}>
            {groups?.map(({ groupId, name, items = [null], threshold }) => (
              <BuilderLine
                collapsed={collapsed}
                items={items}
                key={groupId}
                groupId={groupId}
                GroupItemPlaceholder={GroupItemPlaceholder}
                name={name}
                threshold={threshold}
              />
            ))}
          </div>
        </div>
        {completed && !alwaysOpen ? (
          <ExpandButton collapsed={collapsed} onClick={onClick} name={location} />
        ) : null}
      </div>
    </div>
  );
};

DealBuilder.displayName = 'DealBuilder';

export default DealBuilder;
